// Fade envelope for the AD control track: the programme sound is faded down
// over `fadeInMs` before each description passage, held for its duration and
// faded back up over `fadeOutMs` afterwards. This matches the fixed-gain
// mixdown, which crossfades into the reduced segment ending at event.start
// and out of it starting at event.end.

/**
 * Merges events whose fades would touch into continuous passages.
 *
 * @param {Array<{start: number, end: number}>} events - times in seconds
 * @param {{ fadeInMs?: number, fadeOutMs?: number }} options
 * @returns {Array<{start: number, end: number}>}
 */
export function buildPassages(events, { fadeInMs = 200, fadeOutMs = 200 } = {}) {
    const fadeIn = fadeInMs / 1000;
    const fadeOut = fadeOutMs / 1000;
    const sorted = events
        .filter((event) => Number.isFinite(event?.start) && Number.isFinite(event?.end) && event.end > event.start)
        .map((event) => ({ start: event.start, end: event.end }))
        .sort((a, b) => a.start - b.start);

    const passages = [];
    for (const event of sorted) {
        const last = passages[passages.length - 1];
        if (last && event.start - fadeIn <= last.end + fadeOut) {
            last.end = Math.max(last.end, event.end);
        } else {
            passages.push(event);
        }
    }
    return passages;
}

/**
 * Returns fadeAt(t): the fade amount (0 = none, 1 = full depth) at time t.
 * Assumes it is called with non-decreasing t, as the renderer does.
 *
 * @param {Array<{start: number, end: number}>} passages - from buildPassages
 * @param {{ fadeInMs?: number, fadeOutMs?: number }} options
 * @returns {(t: number) => number}
 */
export function createFadeLookup(passages, { fadeInMs = 200, fadeOutMs = 200 } = {}) {
    const fadeIn = fadeInMs / 1000;
    const fadeOut = fadeOutMs / 1000;
    let index = 0;

    return (t) => {
        while (index < passages.length && t > passages[index].end + fadeOut) {
            index++;
        }
        const passage = passages[index];
        if (!passage) return 0;
        if (t < passage.start - fadeIn) return 0;
        if (t < passage.start) return fadeIn > 0 ? (t - (passage.start - fadeIn)) / fadeIn : 1;
        if (t <= passage.end) return 1;
        return fadeOut > 0 ? 1 - (t - passage.end) / fadeOut : 0;
    };
}
